import React, {
  createContext,
  useContext,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";

/**
 * React 19-compatible replacement for `react-view-pager`.
 *
 * The original library uses the legacy context API (childContextTypes / contextTypes)
 * which was removed in React 19, causing a crash on mount. This shim provides the
 * same public interface — ViewPager, Frame, Track, View — using modern React APIs.
 *
 * All three usages in this codebase have `swipe={false}` and `accessibility={false}`,
 * so swipe gesture support is intentionally omitted. Programmatic navigation via
 * `trackRef.current.next()` / `trackRef.current.prev()` is fully supported.
 */

interface ViewPagerContextValue {
  currentIndex: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  totalViews: number;
  setTotalViews: React.Dispatch<React.SetStateAction<number>>;
}

const ViewPagerContext = createContext<ViewPagerContextValue>({
  currentIndex: 0,
  setCurrentIndex: () => {},
  totalViews: 0,
  setTotalViews: () => {},
});

// ─── ViewPager ────────────────────────────────────────────────────────────────

interface ViewPagerProps extends React.HTMLAttributes<HTMLElement> {
  tag?: React.ElementType;
  children?: React.ReactNode;
}

export function ViewPager({
  tag: Tag = "div",
  children,
  ...props
}: ViewPagerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalViews, setTotalViews] = useState(0);

  return (
    <ViewPagerContext.Provider
      value={{ currentIndex, setCurrentIndex, totalViews, setTotalViews }}
    >
      <Tag {...(props as React.HTMLAttributes<HTMLDivElement>)}>{children}</Tag>
    </ViewPagerContext.Provider>
  );
}

// ─── Frame ───────────────────────────────────────────────────────────────────

interface FrameProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export function Frame({ children, style, ...props }: FrameProps) {
  return (
    <div style={{ overflow: "hidden", ...style }} {...props}>
      {children}
    </div>
  );
}

// ─── Track ───────────────────────────────────────────────────────────────────

export interface TrackHandle {
  next: () => void;
  prev: () => void;
}

/** Type alias for `useRef<Track>(null)` call-site compatibility. */
export type Track = TrackHandle;

interface TrackProps extends React.HTMLAttributes<HTMLDivElement> {
  viewsToShow?: number;
  swipe?: boolean;
  accessibility?: boolean;
  children?: React.ReactNode;
}

export const TrackComponent = forwardRef<TrackHandle, TrackProps>(
  function Track({ children, className, style, viewsToShow: _viewsToShow, swipe: _swipe, accessibility: _accessibility, ...props }, ref) {
    const { currentIndex, setCurrentIndex } = useContext(ViewPagerContext);
    const childArray = React.Children.toArray(children);
    const totalRef = useRef(childArray.length);
    totalRef.current = childArray.length;

    useImperativeHandle(ref, () => ({
      next: () =>
        setCurrentIndex((i) => Math.min(i + 1, totalRef.current - 1)),
      prev: () => setCurrentIndex((i) => Math.max(i - 1, 0)),
    }));

    return (
      <div className={className} style={style} {...props}>
        {childArray.map((child, index) => (
          <div
            key={index}
            style={{ display: index === currentIndex ? "block" : "none" }}
          >
            {child}
          </div>
        ))}
      </div>
    );
  },
);

TrackComponent.displayName = "Track";

/**
 * Named export as `Track` so existing `import { Track }` statements continue to work.
 * The type export `Track` (= `TrackHandle`) covers `useRef<Track>(null)` usage.
 */
export { TrackComponent as Track };

// ─── View ────────────────────────────────────────────────────────────────────

interface ViewProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export function View({ children, ...props }: ViewProps) {
  return <div {...props}>{children}</div>;
}
